import { CDK_BASE_URL } from './../../constants/constants';
import { Company } from '@/context/types';
import { PaginationResponse } from '@/hooks/usePaginationFilter';
import {
  OnboardOrganizationData,
  OrganizationQuery,
  OrganizationResponse,
} from '@/store/slices/organizationsSlice';
import { convertToBase64 } from '@/utils/helpers/convertToBase64';
import { formatUrlWithQueryParams } from '@/utils/helpers/formatUrlWithFilters';
import { apiFetch } from '@final-commerce/jwt-storage-lib';

const COMPANY_URL = `${CDK_BASE_URL}company`;
const COMPANY_MANAGEMENT_URL = `${CDK_BASE_URL}company-management`;

export const getAllOrganizations = async ({
  id,
  filter = { limit: 25, offset: 0 },
}: OrganizationQuery): Promise<PaginationResponse<OrganizationResponse>> => {
  try {
    let url = `${COMPANY_URL}/organizations/${id}`;
    url = formatUrlWithQueryParams(url, filter);
    const res = await apiFetch(url);
    if (!res) {
      throw new Error(`Could not find organizations`);
    }

    const body = await res.json();
    return body;
  } catch (err) {
    console.error('Error fetching organizations', err);
    throw new Error('Error fetching organizations');
  }
};

export const getOrganizationById = async (organizationId: string) => {
  try {
    const res = await apiFetch(`${COMPANY_URL}/${organizationId}`);
    if (!res) {
      throw new Error(`Could not find organizations`);
    }
    const data = await res.json();
    return data;
  } catch (err) {
    console.error('Error fetching organizations', err);
    throw new Error('Error fetching organizations');
  }
};

export const updateOrganization = async ({
  id,
  body,
}: {
  id: string;
  body: Partial<Company>;
}): Promise<Company> => {
  const res = await apiFetch(`${COMPANY_URL}/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res) {
    throw new Error(`Could not update organization`);
  }
  return res.json();
};

export const onboardOrganization = async (body: OnboardOrganizationData): Promise<Company> => {
  try {
    let base64Image = null;

    if (body.owner.image) {
      base64Image = await convertToBase64(body?.owner?.image);
    }

    const payload = {
      ...body,
      owner: {
        ...body.owner,
        image: base64Image,
      },
    };

    const res = await apiFetch(`${COMPANY_MANAGEMENT_URL}/onboard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res) {
      throw new Error(`Could not onboard organization`);
    }

    const data = await res.json();
    return data;
  } catch (err) {
    console.error('Error onboarding organization', err);
    throw new Error('Error onboarding organization');
  }
};
